/** Formato usado pelo hook useMenuPermissions e pelo dashboard. */
export interface MenuPermissionsByRole {
  [menuId: string]: boolean;
}

export interface StoredMenuPermissions {
  [role: string]: MenuPermissionsByRole;
}

/** Formato legado da página MenuPermissions (array). */
export interface LegacyRoleMenuPermissions {
  role: string;
  permissions: MenuPermissionsByRole;
}

export const MENU_PERMISSIONS_STORAGE_KEY = 'menu-permissions';

export const defaultMenuIdsByRole: Record<string, string[]> = {
  admin: [
    'dashboard',
    'usuarios',
    'permissoes-menu',
    'clientes',
    'funcionarios',
    'servicos',
    'produtos',
    'propriedades',
    'agendamentos',
    'financeiro',
    'configuracoes',
    'relatorios',
    'notificacoes',
    'zonas',
    'ajuda',
  ],
  supervisor: [
    'dashboard',
    'clientes',
    'funcionarios',
    'servicos',
    'produtos',
    'propriedades',
    'agendamentos',
    'financeiro',
    'relatorios',
    'notificacoes',
    'zonas',
    'ajuda',
  ],
  employee: [
    'dashboard',
    'clientes',
    'funcionarios',
    'servicos',
    'produtos',
    'propriedades',
    'agendamentos',
    'notificacoes',
    'ajuda',
  ],
  client: ['dashboard', 'agendamentos', 'notificacoes', 'ajuda'],
};

export const allMenuItemIds = [
  'dashboard',
  'usuarios',
  'permissoes-menu',
  'clientes',
  'funcionarios',
  'servicos',
  'produtos',
  'propriedades',
  'agendamentos',
  'financeiro',
  'configuracoes',
  'relatorios',
  'notificacoes',
  'zonas',
  'ajuda',
] as const;

export function buildDefaultStoredMenuPermissions(): StoredMenuPermissions {
  const result: StoredMenuPermissions = {};
  Object.keys(defaultMenuIdsByRole).forEach(role => {
    result[role] = {};
    allMenuItemIds.forEach(menuId => {
      result[role][menuId] = defaultMenuIdsByRole[role]?.includes(menuId) ?? false;
    });
  });
  return result;
}

function isLegacyArrayFormat(value: unknown): value is LegacyRoleMenuPermissions[] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    typeof value[0] === 'object' &&
    value[0] !== null &&
    'role' in value[0] &&
    'permissions' in value[0]
  );
}

function isStoredObjectFormat(value: unknown): value is StoredMenuPermissions {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  return Object.keys(defaultMenuIdsByRole).every(
    role =>
      Object.prototype.hasOwnProperty.call(value, role) &&
      typeof (value as StoredMenuPermissions)[role] === 'object' &&
      (value as StoredMenuPermissions)[role] !== null &&
      !Array.isArray((value as StoredMenuPermissions)[role])
  );
}

/** Converte array legado (página MenuPermissions) para o mapa por role. */
export function legacyArrayToStored(value: LegacyRoleMenuPermissions[]): StoredMenuPermissions {
  const result: StoredMenuPermissions = {};
  value.forEach(entry => {
    if (entry?.role && entry.permissions && typeof entry.permissions === 'object') {
      result[entry.role] = { ...entry.permissions };
    }
  });
  return result;
}

/**
 * Mescla permissões salvas com os defaults: chaves ausentes recebem o valor padrão do role.
 * Corrige TWA/PWA com localStorage antigo ou parcial (ex.: menu Usuários sumindo).
 */
export function mergeStoredMenuPermissionsWithDefaults(
  stored: StoredMenuPermissions
): StoredMenuPermissions {
  const defaults = buildDefaultStoredMenuPermissions();
  const merged: StoredMenuPermissions = {};

  Object.keys(defaultMenuIdsByRole).forEach(role => {
    const roleStored = stored[role];
    merged[role] = {};
    allMenuItemIds.forEach(menuId => {
      const storedValue = roleStored?.[menuId];
      if (typeof storedValue === 'boolean') {
        merged[role][menuId] = storedValue;
      } else {
        merged[role][menuId] = defaults[role][menuId];
      }
    });
  });

  return merged;
}

/** Normaliza qualquer formato persistido para StoredMenuPermissions. */
export function parseStoredMenuPermissions(raw: string | null): StoredMenuPermissions {
  const defaults = buildDefaultStoredMenuPermissions();
  if (!raw) return defaults;

  try {
    const parsed: unknown = JSON.parse(raw);
    if (isLegacyArrayFormat(parsed)) {
      const fromLegacy = legacyArrayToStored(parsed);
      if (Object.keys(fromLegacy).length === 0) return defaults;
      return mergeStoredMenuPermissionsWithDefaults(fromLegacy);
    }
    if (isStoredObjectFormat(parsed)) {
      return mergeStoredMenuPermissionsWithDefaults(parsed);
    }
  } catch {
    // formato inválido
  }

  return defaults;
}

export function readStoredMenuPermissions(): StoredMenuPermissions {
  if (typeof window === 'undefined') return buildDefaultStoredMenuPermissions();
  return parseStoredMenuPermissions(localStorage.getItem(MENU_PERMISSIONS_STORAGE_KEY));
}

export function writeStoredMenuPermissions(permissions: StoredMenuPermissions): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(MENU_PERMISSIONS_STORAGE_KEY, JSON.stringify(permissions));
}

/** Converte para o array usado na UI da página MenuPermissions. */
export function storedToLegacyArray(
  stored: StoredMenuPermissions,
  menuIds: string[]
): LegacyRoleMenuPermissions[] {
  return Object.keys(defaultMenuIdsByRole).map(role => ({
    role,
    permissions: menuIds.reduce(
      (acc, menuId) => {
        acc[menuId] = stored[role]?.[menuId] ?? false;
        return acc;
      },
      {} as MenuPermissionsByRole
    ),
  }));
}
